// Stripe Connect utilities for professional onboarding and payment management

export interface ConnectedAccount {
  id: string
  accountId: string
  email: string
  country: string
  accountType: 'express' | 'standard'
  chargesEnabled: boolean
  payoutsEnabled: boolean
  detailsSubmitted: boolean
  requirementsCurrentlyDue: string[]
  requirementsEventuallyDue: string[]
  onboardingUrl?: string
  dashboardUrl?: string
  created: string
  updated: string
}

export interface PaymentTransfer {
  id: string
  amount: number
  currency: string
  destinationAccountId: string
  sourceTransactionId: string
  platformFee: number
  professionalEarnings: number
  status: 'pending' | 'completed' | 'failed'
  sessionId: string
  created: string
}

export interface EscrowPayment {
  id: string
  clientId: string
  professionalId: string
  sessionId: string
  amount: number
  currency: string
  platformFee: number
  status: 'held' | 'released' | 'refunded'
  paymentIntentId: string
  releaseDate?: string
  refundReason?: string
  created: string
}

// Create a Stripe Connect Express account for a professional
export const createConnectedAccount = async (
  professionalId: string,
  email: string,
  country: string = 'US'
): Promise<ConnectedAccount> => {
  // TODO: In production, this should call your backend API
  /* Production implementation:
  const response = await fetch('/api/connect/accounts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      professionalId,
      email,
      country,
      type: 'express'
    })
  })

  if (!response.ok) {
    throw new Error('Failed to create connected account')
  }

  return response.json()
  */

  // Mock implementation for development
  const mockAccount: ConnectedAccount = {
    id: `prof_${professionalId}_${Date.now()}`,
    accountId: `acct_mock_${Math.random().toString(36).substr(2, 9)}`,
    email,
    country,
    accountType: 'express',
    chargesEnabled: false,
    payoutsEnabled: false,
    detailsSubmitted: false,
    requirementsCurrentlyDue: ['individual.first_name', 'individual.last_name', 'individual.dob.day'],
    requirementsEventuallyDue: ['external_account'],
    onboardingUrl: `https://connect.stripe.com/express/onboard/${Math.random().toString(36)}`,
    created: new Date().toISOString(),
    updated: new Date().toISOString()
  }

  return mockAccount
}

// Get onboarding link for professional to complete account setup
export const getOnboardingLink = async (
  _professionalId: string,
  _returnUrl: string,
  _refreshUrl: string
): Promise<string> => {
  // TODO: In production, this should call your backend API
  /* Production implementation:
  const response = await fetch('/api/connect/onboarding-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      professionalId,
      returnUrl,
      refreshUrl
    })
  })

  if (!response.ok) {
    throw new Error('Failed to create onboarding link')
  }

  const data = await response.json()
  return data.url
  */

  // Mock implementation
  return `https://connect.stripe.com/express/onboard/mock_${Math.random().toString(36)}`
}

// Check the status of a connected account
export const getAccountStatus = async (professionalId: string): Promise<ConnectedAccount | null> => {
  // TODO: In production, this should call your backend API
  /* Production implementation:
  const response = await fetch(`/api/connect/accounts/${professionalId}`)

  if (response.status === 404) {
    return null
  }

  if (!response.ok) {
    throw new Error('Failed to get account status')
  }

  return response.json()
  */

  // Mock implementation - simulate different account states
  const random = Math.random()

  if (random < 0.3) {
    // No account created yet
    return null
  }

  if (random < 0.6) {
    // Account created but onboarding incomplete
    return {
      id: `prof_${professionalId}_mock`,
      accountId: `acct_mock_${professionalId}`,
      email: 'professional@example.com',
      country: 'US',
      accountType: 'express',
      chargesEnabled: false,
      payoutsEnabled: false,
      detailsSubmitted: false,
      requirementsCurrentlyDue: ['individual.first_name', 'individual.last_name'],
      requirementsEventuallyDue: ['external_account'],
      onboardingUrl: `https://connect.stripe.com/express/onboard/mock_${professionalId}`,
      created: new Date().toISOString(),
      updated: new Date().toISOString()
    }
  }

  // Account fully set up
  return {
    id: `prof_${professionalId}_mock`,
    accountId: `acct_mock_${professionalId}`,
    email: 'professional@example.com',
    country: 'US',
    accountType: 'express',
    chargesEnabled: true,
    payoutsEnabled: true,
    detailsSubmitted: true,
    requirementsCurrentlyDue: [],
    requirementsEventuallyDue: [],
    dashboardUrl: `https://dashboard.stripe.com/express/acct_mock_${professionalId}`,
    created: new Date().toISOString(),
    updated: new Date().toISOString()
  }
}

// Create a payment transfer to professional after session completion
export const createPaymentTransfer = async (
  sessionId: string,
  professionalId: string,
  amount: number,
  currency: string = 'usd',
  platformFeePercent: number = 15
): Promise<PaymentTransfer> => {
  const platformFee = Math.round(amount * (platformFeePercent / 100))
  const professionalEarnings = amount - platformFee

  // TODO: In production, this should call your backend API
  /* Production implementation:
  const response = await fetch('/api/payments/transfer', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      professionalId,
      amount,
      currency,
      platformFee,
      professionalEarnings
    })
  })

  if (!response.ok) {
    throw new Error('Failed to create payment transfer')
  }

  return response.json()
  */

  // Mock implementation
  const transfer: PaymentTransfer = {
    id: `tr_mock_${Math.random().toString(36).substr(2, 9)}`,
    amount,
    currency,
    destinationAccountId: `acct_mock_${professionalId}`,
    sourceTransactionId: `pi_mock_${sessionId}`,
    platformFee,
    professionalEarnings,
    status: 'completed',
    sessionId,
    created: new Date().toISOString()
  }

  return transfer
}

// Hold payment in escrow until session completion
export const holdPaymentInEscrow = async (
  sessionId: string,
  clientId: string,
  professionalId: string,
  paymentIntentId: string,
  amount: number,
  currency: string = 'usd',
  platformFeePercent: number = 15
): Promise<EscrowPayment> => {
  const platformFee = Math.round(amount * (platformFeePercent / 100))

  // TODO: In production, this should call your backend API
  /* Production implementation:
  const response = await fetch('/api/payments/escrow', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      clientId,
      professionalId,
      paymentIntentId,
      amount,
      currency,
      platformFee
    })
  })

  if (!response.ok) {
    throw new Error('Failed to hold payment in escrow')
  }

  return response.json()
  */

  // Mock implementation
  const escrowPayment: EscrowPayment = {
    id: `escrow_mock_${Math.random().toString(36).substr(2, 9)}`,
    clientId,
    professionalId,
    sessionId,
    amount,
    currency,
    platformFee,
    status: 'held',
    paymentIntentId,
    created: new Date().toISOString()
  }

  return escrowPayment
}

// Release payment from escrow after session completion
export const releaseEscrowPayment = async (
  escrowPaymentId: string
): Promise<{ transfer: PaymentTransfer; escrow: EscrowPayment }> => {
  // TODO: In production, this should call your backend API
  /* Production implementation:
  const response = await fetch(`/api/payments/escrow/${escrowPaymentId}/release`, {
    method: 'POST'
  })

  if (!response.ok) {
    throw new Error('Failed to release escrow payment')
  }

  return response.json()
  */

  // Mock implementation
  const now = new Date().toISOString()

  const escrow: EscrowPayment = {
    id: escrowPaymentId,
    clientId: 'client_mock',
    professionalId: 'prof_mock',
    sessionId: 'session_mock',
    amount: 10000, // $100.00 in cents
    currency: 'usd',
    platformFee: 1500, // $15.00 in cents
    status: 'released',
    paymentIntentId: 'pi_mock',
    releaseDate: now,
    created: now
  }

  const transfer: PaymentTransfer = {
    id: `tr_mock_${Math.random().toString(36).substr(2, 9)}`,
    amount: escrow.amount,
    currency: escrow.currency,
    destinationAccountId: `acct_mock_${escrow.professionalId}`,
    sourceTransactionId: escrow.paymentIntentId,
    platformFee: escrow.platformFee,
    professionalEarnings: escrow.amount - escrow.platformFee,
    status: 'completed',
    sessionId: escrow.sessionId,
    created: now
  }

  return { transfer, escrow }
}

// Get professional earnings summary
export const getProfessionalEarnings = async (
  professionalId: string,
  _startDate?: string,
  _endDate?: string
): Promise<{
  totalEarnings: number
  pendingPayouts: number
  completedPayouts: number
  platformFees: number
  transactionCount: number
  transactions: PaymentTransfer[]
}> => {
  // TODO: In production, this should call your backend API
  /* Production implementation:
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)

  const response = await fetch(`/api/professionals/${professionalId}/earnings?${params}`)

  if (!response.ok) {
    throw new Error('Failed to get professional earnings')
  }

  return response.json()
  */

  // Mock implementation
  const mockTransactions: PaymentTransfer[] = [
    {
      id: 'tr_mock_1',
      amount: 10000,
      currency: 'usd',
      destinationAccountId: `acct_mock_${professionalId}`,
      sourceTransactionId: 'pi_mock_1',
      platformFee: 1500,
      professionalEarnings: 8500,
      status: 'completed',
      sessionId: 'session_1',
      created: new Date(Date.now() - 86400000).toISOString() // 1 day ago
    },
    {
      id: 'tr_mock_2',
      amount: 15000,
      currency: 'usd',
      destinationAccountId: `acct_mock_${professionalId}`,
      sourceTransactionId: 'pi_mock_2',
      platformFee: 2250,
      professionalEarnings: 12750,
      status: 'completed',
      sessionId: 'session_2',
      created: new Date(Date.now() - 172800000).toISOString() // 2 days ago
    }
  ]

  const totalEarnings = mockTransactions.reduce((sum, t) => sum + t.professionalEarnings, 0)
  const platformFees = mockTransactions.reduce((sum, t) => sum + t.platformFee, 0)

  return {
    totalEarnings,
    pendingPayouts: 0,
    completedPayouts: totalEarnings,
    platformFees,
    transactionCount: mockTransactions.length,
    transactions: mockTransactions
  }
}